import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Carregando from '@/components/Carregando';
import PainelLead from '@/components/leads/PainelLead';
import DialogoEtapa from '@/components/leads/DialogoEtapa';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { GRUPOS_PRAZO, classificarPrazo } from '@/leads/cadencia';
import { SECOES, etapaDaTarefa, responsavelDaTarefa, secaoDaTarefa, secoesAbertasPorFuncao, tarefaDoResponsavel } from '@/leads/fila';
import { TEMPERATURAS, etapaPorId } from '@/lib/constants';
import { formatarTelefone } from '@/lib/telefone';
import { funcoesDe, vendedores } from '@/equipe/funcoes';
import { concluirTarefa, ouvirProximosContatos, reagendarTarefa } from '@/services/tarefasService';
import PlacarDoDia from '@/components/produtividade/PlacarDoDia';
import { placarDoDia } from '@/produtividade/placar';
import { expedienteDaClinica } from '@/produtividade/expediente';
import { feriadosDaClinica } from '@/produtividade/feriados';
import { PRODUTIVIDADE_LIBERADA } from '@/produtividade/disponibilidade';
import { diaDaData } from '@/produtividade/dia';
import { normalizarMetasPessoa } from '@/metas/calculo';
import { buscarAgendamentosEntre, buscarAgendamentosFuturos, buscarInteracoesEntre } from '@/services/produtividadeService';
import { ouvirMetas } from '@/services/metasService';
import { AvisoMetas } from '@/components/AvisoCarga';
import { tratarErro } from '@/lib/erros';
import AvisoCarga from '@/components/AvisoCarga';
import { useMesAtual } from '@/lib/mesAtual';

const exibirHora = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  return data ? format(data, 'dd/MM HH:mm') : '—';
};

const amanhaCedo = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
};

/** Plural simples dos rótulos do cabeçalho. */
const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

/**
 * A primeira tela que o atendente abre de manhã (PLANO 4.4): a fila de
 * contatos pendentes, agrupada por urgência — atrasadas primeiro.
 *
 * ★ VV (decisão do dono, 20/set/2026) — DUAS SEÇÕES: **Agendamentos** (quem não
 * agendou / não compareceu — trabalho da SDR) e **Follow-up** (quem compareceu e
 * não fechou — trabalho do negociador). ★ PC-1 (17/out): "Outros contatos" acabou —
 * novo e agendado vão para Agendamentos, fechado para Follow-up. A seção é DERIVADA da etapa
 * ATUAL do lead (`secaoDaTarefa`): um listener só, a mesma consulta de sempre, a
 * divisão no cliente. A abertura padrão segue a FUNÇÃO de quem olha — é
 * preferência de tela: toda seção é renderizada para quem tem acesso à página;
 * `funcoes` não decide acesso (VP/VU).
 */
export default function ProximosContatosPage() {
  const [falhaPlacar, setFalhaPlacar] = useState(false); // ★ WT lote 1b
  const [falhaMetas, setFalhaMetas] = useState(false); // ★ WT lote 1b
  // ★★★ WU-2/P3 — as metas vieram do cache e o servidor não confirmou. ⚠ Não é falha: é
  // "pode estar velho", que é exatamente o que ninguém dizia em 16/out.
  const [metasVelhas, setMetasVelhas] = useState(false);
  /**
   * ★★★ WU-2/P8 — o mês ACOMPANHA A VIRADA. ⚠ Era `mesNaClinica(new Date())` dentro do
   * efeito, com o mês fora das dependências: uma aba montada em 31/10 assinava outubro
   * para sempre.
   */
  const mesDoPlacar = useMesAtual();
  // ★ A-7a: `isGestor` desce para o `PlacarDoDia` — o link "sem meta" é por papel.
  const { usuario, papel, isGestor } = useAuth();
  const {
    clinicaId, autor, equipe, leads, abrirLead, leadAberto, clinica, carregando: leadsCarregando, definirDesatualizado,
  } = useLeads();

  /**
   * ★★★ WT lote 1b — A FILA NÃO DESENHA ANTES DE SABER DE QUEM É CADA TAREFA.
   *
   * ⚠⚠ O relato do dono (06/out, login da Jéssica): por 3–4 s a tela mostrava **201**
   * tarefas e leads de **todas as SDRs**; depois trocava para 182. ★ Medido, a causa não é o
   * papel nem o filtro — os dois já estão certos no primeiro render (`RequireAuth` espera o
   * `carregando` do Auth, e as claims chegam no mesmo tique).
   *
   * ★★★ A causa é `responsavelDaTarefa` (`leads/fila.js:96`): quando a tarefa **não tem**
   * `responsavelId` gravado, ela herda o dono do **lead** — e **sem a lista de leads
   * carregada** devolve `VAZIO`. ⚠ Aí `tarefaDoResponsavel` faz `return !dono || …`, trata
   * como **"sem responsável"** e **MANTÉM** a tarefa (decisão do dono: trabalho órfão não
   * pode exigir trocar de filtro).
   *
   * ⚠⚠ O resultado é que **"ainda não sei de quem é" fica indistinguível de "não tem dono"**
   * — e por alguns segundos a SDR vê a fila de todo mundo. **É vazamento por corrida, não
   * por regra.**
   *
   * ★ O conserto é não concluir: enquanto os leads não chegaram, a tela mostra esqueleto.
   * ⚠ Não dá para "filtrar melhor" — a informação necessária simplesmente não está ali.
   */
  const seiDeQuemSaoAsTarefas = !leadsCarregando;

  /**
   * ★★ WO lote 3b — O PLACAR DO DIA, no topo desta tela.
   *
   * ⚠⚠ Esta é a tela que a SDR deixa ABERTA O DIA INTEIRO — o custo aqui pesa diferente do
   * custo da grade, que o gestor abre de vez em quando. ★ Por isso a janela é de **4 dias**
   * (≈260 documentos), não de 90; e os agendamentos vêm só da **semana**.
   *
   * ★ As tarefas abertas já estão na tela (`ouvirProximosContatos`) — custo zero.
   * ⚠ Escondido pela MESMA bandeira dos Indicadores: o gestor vê, a equipe ainda não.
   */
  const [interacoes4d, setInteracoes4d] = useState([]);
  /**
   * ★★ WT lote 1b — o placar só desenha quando os dados dele chegaram.
   *
   * ⚠ Sem isto, ele renderizava com as listas VAZIAS (tudo zerado) e depois se corrigia: a
   * tela **pulava** e, por um instante, mostrava um placar **falso** — zero de tudo.
   * ★ Esqueleto da MESMA altura no intervalo.
   */
  const [placarPronto, setPlacarPronto] = useState(false);
  const [agendamentosSemana, setAgendamentosSemana] = useState([]);
  // ★ PC-2 — consultas com `dataHora ≥ agora`: lead agendado com consulta futura não é cobrado.
  const [agendamentosFuturos, setAgendamentosFuturos] = useState([]);
  const [metasDoMes, setMetasDoMes] = useState(null);
  const vejoOPlacar = PRODUTIVIDADE_LIBERADA || papel === 'gestor' || papel === 'superadmin';

  useEffect(() => {
    if (!clinicaId || !vejoOPlacar) return undefined;
    let valendo = true;
    const agora = new Date();
    // ★ 4 dias: hoje + os 3 que a pendência de follow-up precisa olhar para trás.
    const de = new Date(agora.getTime() - 4 * 864e5);
    // ⚠ A semana começa na segunda; 7 dias para trás a cobrem com folga.
    const deSemana = new Date(agora.getTime() - 8 * 864e5);
    Promise.all([
      buscarInteracoesEntre(clinicaId, de, agora),
      buscarAgendamentosEntre(clinicaId, deSemana, agora),
      // ★ PC-2: no MESMO Promise.all — a falha dela é a falha do placar, já tratada (WT 1b).
      buscarAgendamentosFuturos(clinicaId, agora),
    ])
      .then(([is, ags, futuros]) => {
        if (!valendo) return;
        setInteracoes4d(is);
        setAgendamentosSemana(ags);
        setAgendamentosFuturos(futuros);
        setPlacarPronto(true);
      })
      .catch(tratarErro('o placar do dia', () => setFalhaPlacar(true), 'service'));
    return () => { valendo = false; };
  }, [clinicaId, vejoOPlacar]);

  useEffect(() => {
    if (!clinicaId || !vejoOPlacar) return undefined;
    // ★★ WT 1b: era só console — o placar do dia ficava sem alvo, calado.
    // ★★★ WU-2: `mesDoPlacar` está nas dependências — é o que faz a virada do mês trocar a
    // assinatura. ⚠ E o 5º argumento é o aviso de "não confirmado pelo servidor" (P3).
    return ouvirMetas(clinicaId, mesDoPlacar, setMetasDoMes, () => setFalhaMetas(true), setMetasVelhas);
  }, [clinicaId, vejoOPlacar, mesDoPlacar]);

  /**
   * ★★★ WU-2b — a meta do dia não desenha mais a PRÓPRIA faixa: avisa o provider, que mostra
   * uma só para todos os assuntos. ⚠ `!falhaMetas` continua: com o vermelho das metas na
   * tela, o azul do mesmo assunto seria ruído. ★ Ao sair da tela, o assunto é apagado —
   * senão a faixa ficaria acesa no Funil por causa de uma meta que ninguém está olhando.
   */
  useEffect(() => {
    definirDesatualizado?.('a meta do dia', metasVelhas && !falhaMetas);
    return () => definirDesatualizado?.('a meta do dia', false);
  }, [definirDesatualizado, metasVelhas, falhaMetas]);

  // SDR abre já na própria fila; gestor vê a clínica inteira por padrão.
  // VV: o filtro é aplicado no CLIENTE (`tarefaDoResponsavel`), para a pessoa ver
  // também as tarefas SEM responsável — a consulta não muda.
  /** ★ A-2 / E6 — vazio = qualquer temperatura, como em `FILTROS_VAZIOS` do funil. */
  const [temperatura, setTemperatura] = useState('');
  const [responsavelId, setResponsavelId] = useState(() =>
    papel === 'sdr' ? (usuario?.uid ?? '') : '',
  );
  const [tarefas, setTarefas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [pendente, setPendente] = useState(null);

  /**
   * Seções abertas: começa pela função de quem olha (só negociador → Follow-up;
   * só SDR → Agendamentos; gestor/ambas → as duas). Estado de sessão, sem
   * persistir — é preferência do momento, como o acordeão do menu.
   */
  const [abertas, setAbertas] = useState(() =>
    secoesAbertasPorFuncao(funcoesDe(equipe?.find((u) => u.id === usuario?.uid))),
  );
  const alternarSecao = (id) =>
    setAbertas((atuais) => {
      const proximo = new Set(atuais);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  useEffect(() => {
    if (!clinicaId) return undefined;
    setCarregando(true);
    return ouvirProximosContatos(
      clinicaId,
      {},
      (lista) => {
        setTarefas(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir a fila de contatos:', e);
        setErro('Não foi possível carregar a fila de contatos.');
        setCarregando(false);
      },
    );
  }, [clinicaId]);

  // VV-c1: o filtro por pessoa conta as tarefas herdadas do SDR do lead — por isso `leads` entra.
  /** ★ Só o próprio cartão para quem não é gestor — a regra é por `uid`, não por nome. */
  const cartoes = useMemo(() => {
    if (!vejoOPlacar) return [];
    const metas = {};
    for (const [uid, m] of Object.entries(metasDoMes?.metasPorSdr ?? {})) metas[uid] = normalizarMetasPessoa(m);
    const agora = new Date();
    const vejoTudo = papel === 'gestor' || papel === 'superadmin';
    const todos = placarDoDia({
      equipe, uid: usuario?.uid, vejoTudo,
      agora, hoje: diaDaData(agora),
      interacoes: interacoes4d, agendamentos: agendamentosSemana, tarefas, leads, agendamentosFuturos,
      metasDia: metas, expediente: expedienteDaClinica(clinica?.config),
      // ★ 02/11/2026 — feriado sem meta: "Feriado — sem meta hoje", sem semáforo de ritmo
      feriados: feriadosDaClinica(clinica?.config),
    });
    /**
     * ★★ PC-1 (decisão do dono, 17/out/2026) — o select "Responsável" filtra os CARTÕES
     * também, mas SÓ para quem vê todos. ⚠ Para a SDR o filtro não se aplica: ela já vê só
     * o dela, e escolher uma colega daria ZERO cartões — perderia o próprio ritmo por ter
     * olhado a fila de outra pessoa.
     */
    return vejoTudo && responsavelId ? todos.filter((c) => c.id === responsavelId) : todos;
  }, [vejoOPlacar, equipe, usuario?.uid, papel, interacoes4d, agendamentosSemana, agendamentosFuturos, tarefas, leads, metasDoMes, clinica, responsavelId]);

  /**
   * ★★★ A-2 / E6 — FILTRO DE TEMPERATURA, reusando o `<select>` que já existe.
   *
   * ★ A temperatura mora no LEAD, não na tarefa — então o filtro resolve o lead da tarefa pelo
   * mesmo mapa que o resto da tela já usa.
   *
   * ⚠⚠⚠ **O QUE A MEDIÇÃO DIZ, e o dono precisa saber:** `temperatura` está preenchida em
   * **8.164 de 8.164** leads (100%), ★ mas **8.143 são `morno`** — o padrão. ★★★ Só **21 leads**
   * têm outra coisa (12 fervendo, 7 frio, 2 quente). ⚠ Logo o filtro **vai funcionar e vai
   * parecer quebrado**: *"filtrei por quente e não apareceu nada"* é o que a equipe vai dizer,
   * porque **só 2 leads são quentes** — e os dois podem não ter tarefa aberta.
   * ★★ O número não diz que o filtro falta: diz que **a equipe não está marcando temperatura**.
   */
  const visiveis = useMemo(
    () => tarefas.filter((t) => {
      if (!tarefaDoResponsavel(t, responsavelId, leads)) return false;
      if (!temperatura) return true;
      // ⚠ Tarefa cujo lead não está na lista carregada NÃO passa no filtro específico:
      // ★ mostrar o que não se pôde conferir seria dizer "este é quente" sem saber.
      return leads.find((l) => l.id === t.leadId)?.temperatura === temperatura;
    }),
    [tarefas, responsavelId, leads, temperatura],
  );

  /** Cada seção com as suas tarefas, já agrupadas por prazo. */
  const secoes = useMemo(() => {
    const porSecao = new Map(SECOES.map((s) => [s.id, []]));
    for (const tarefa of visiveis) porSecao.get(secaoDaTarefa(tarefa, leads)).push(tarefa);
    return SECOES.map((s) => {
      const lista = porSecao.get(s.id);
      const mapa = new Map(GRUPOS_PRAZO.map((g) => [g.id, []]));
      for (const tarefa of lista) mapa.get(classificarPrazo(tarefa.dataHora))?.push(tarefa);
      const grupos = GRUPOS_PRAZO.map((g) => ({ ...g, tarefas: mapa.get(g.id) ?? [] })).filter(
        (g) => g.tarefas.length > 0,
      );
      return { ...s, tarefas: lista, grupos };
    });
  }, [visiveis, leads]);

  const totalPor = (id) => secoes.find((s) => s.id === id)?.tarefas.length ?? 0;
  const atrasadas = visiveis.filter((t) => classificarPrazo(t.dataHora) === 'atrasada').length;

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  // ★★★ WT 1b: o `||` é o conserto do vazamento. ⚠ `carregando` (as tarefas) não basta:
  // elas podem chegar ANTES dos leads, e é nessa janela que a fila de outra pessoa aparece.
  if (carregando || !seiDeQuemSaoAsTarefas) return <Carregando texto="Carregando a fila do dia…" />;

  return (
    <div className={leadAberto ? 'pagina com-painel' : 'pagina'}>
      <div className="pagina-principal">
        <header className="pagina-cabecalho com-acao">
          <div>
            <h1>Próximos contatos</h1>
            {/* ★★ WT 1b — a faixa não empurra a grade (`role="status"`, uma linha). */}
            {falhaMetas && <AvisoMetas />}
            {/* ★★ WT 1b: o placar do dia não carregou — antes ele simplesmente não aparecia. */}
            {falhaPlacar && <AvisoCarga oQue="o placar do dia" />}
            {/* VV: "contatos pendentes" — "follow-up" agora é o nome de UMA seção. O total
                soma as duas seções (★ PC-1: "Outros" acabou). */}
            <p className="painel-sub">
              {plural(visiveis.length, 'contato pendente', 'contatos pendentes')}
              {` · ${plural(totalPor('agendamentos'), 'agendamento', 'agendamentos')}`}
              {` · ${plural(totalPor('followup'), 'follow-up', 'follow-ups')}`}
              {atrasadas > 0 ? ` · ${plural(atrasadas, 'atrasado', 'atrasados')}` : ''}
            </p>
          </div>
          <div className="filtros-fila">
            {/* VV: SDR ∪ negociador — a seção Follow-up é trabalho do negociador, e a
                fila dele também se filtra aqui. Escolher uma pessoa mostra as dela E
                as sem responsável. */}
            <select
              value={responsavelId}
              onChange={(e) => setResponsavelId(e.target.value)}
              aria-label="Responsável"
            >
              <option value="">Toda a equipe</option>
              {vendedores(equipe).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>

            {/* ★ E6 — o MESMO select do funil e da lista de leads (mesmas opções, mesmo
                rótulo de acessibilidade): ⚠ duas réguas de temperatura na casa virariam
                duas listas de opções que divergem. */}
            <select
              value={temperatura}
              onChange={(e) => setTemperatura(e.target.value)}
              aria-label="Temperatura"
            >
              <option value="">Qualquer temperatura</option>
              {TEMPERATURAS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        {/* ★★ WO lote 3b — O PLACAR DO DIA, no topo. Decisão do desenho aprovado: fica onde
            a SDR já está o dia inteiro, e não numa tela que ela precise abrir.
            ⚠ Escondido pela mesma bandeira dos Indicadores — o gestor vê, a equipe ainda não.
            ★★ A-2: o expediente NÃO vem mais por prop — cada cartão traz o DA PESSOA
            (`cartaoDoDia`); ⚠ o da clínica entra em `placarDoDia` acima, como reserva. */}
        {vejoOPlacar && (placarPronto ? (
          <PlacarDoDia cartoes={cartoes} agora={new Date()} isGestor={isGestor} />
        ) : (
          /* ★★ WT 1b — esqueleto com a MESMA altura: a tela não pula quando o placar chega.
             ⚠ `aria-hidden` porque não é conteúdo, é espaço reservado. */
          <div className="placar-esqueleto" aria-hidden="true" data-esqueleto="placar" />
        ))}

        {erro && <p className="erro">{erro}</p>}

        {secoes.map((secao) => {
          const aberta = abertas.has(secao.id);
          return (
            <section key={secao.id} className="secao-fila" data-secao={secao.id}>
              <button
                type="button"
                className="secao-titulo"
                onClick={() => alternarSecao(secao.id)}
                aria-expanded={aberta}
              >
                <span>{secao.label}</span>
                <span className="grupo-contagem">{secao.tarefas.length}</span>
              </button>

              {aberta && secao.tarefas.length === 0 && (
                <div className="cartao">
                  <p className="em-breve">{secao.vazio}</p>
                </div>
              )}

              {aberta && secao.grupos.map((grupo) => (
          <div key={grupo.id} className="grupo-fila">
            <h2 className={grupo.id === 'atrasada' ? 'grupo-titulo atrasado' : 'grupo-titulo'}>
              {grupo.label}
              <span className="grupo-contagem">{grupo.tarefas.length}</span>
            </h2>

            <ul className="fila">
              {grupo.tarefas.map((tarefa) => {
                const etapa = etapaDaTarefa(tarefa, leads);
                // VV-c1: o gravado manda; vazio herda o SDR ATUAL do lead; só sem os dois é órfã.
                const dono = responsavelDaTarefa(tarefa, leads);
                return (
                  <li key={`${tarefa.leadId}-${tarefa.id}`} className="item-fila">
                    <div className="item-quando">{exibirHora(tarefa.dataHora)}</div>

                    <div className="item-corpo">
                      <button
                        type="button"
                        className="item-lead"
                        onClick={() => abrirLead(tarefa.leadId)}
                      >
                        {tarefa.leadNome || 'Lead sem nome'}
                      </button>
                      <p className="item-titulo">
                        {tarefa.titulo}
                        {tarefa.origem === 'comparecimento' && (
                          <span className="indicador-nota"> · automático</span>
                        )}
                      </p>
                      <p className="item-meta">
                        {etapa && (
                          <span
                            className="pilula pequena"
                            style={{ background: etapaPorId(etapa).cor }}
                          >
                            {etapaPorId(etapa).label}
                          </span>
                        )}
                        {tarefa.leadTelefone && (
                          <a href={`tel:${tarefa.leadTelefone}`}>
                            {formatarTelefone(tarefa.leadTelefone)}
                          </a>
                        )}
                        {/* VV: tarefa órfã aparece para todo mundo, e marcada. */}
                        {dono.id ? (
                          <span title={dono.herdado ? 'Pelo SDR atual do lead' : undefined}>{dono.nome}</span>
                        ) : (
                          <span className="pilula pequena sem-responsavel">sem responsável</span>
                        )}
                      </p>
                    </div>

                    <div className="item-acoes">
                      <button
                        type="button"
                        className="btn-secundario"
                        disabled={ocupado}
                        onClick={() => executar(() => concluirTarefa(clinicaId, tarefa, autor))}
                      >
                        Concluir
                      </button>
                      <button
                        type="button"
                        className="btn-texto"
                        disabled={ocupado}
                        onClick={() =>
                          executar(() => reagendarTarefa(clinicaId, tarefa, amanhaCedo(), autor))
                        }
                      >
                        Adiar p/ amanhã
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
              ))}
            </section>
          );
        })}
      </div>

      <PainelLead aoPedirEtapa={setPendente} />
      {/* Regressão da V2 corrigida aqui: o diálogo é props-only e esta página
          o montava sem clinicaId/autor — mover para fechado/perdido quebrava. */}
      <DialogoEtapa
        grade={clinica?.config?.agendaAvaliacao}
        config={clinica?.config ?? null}
        pendente={pendente}
        aoFechar={() => setPendente(null)}
        clinicaId={clinicaId}
        autor={autor}
        equipe={equipe}
      />
    </div>
  );
}
